import { Box, Button, Grid, InputAdornment } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AsyncSelect from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import Text from "../../../../../components/Text";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddPayments } from "../../../../../queries/billings/payments/use-add-payments";
import { useGetInsuranceByPatientId } from "../../../../../queries/insurance/use-get-patient-insurance-patient-uuid";
import { useSearchPatientList } from "../../../../../queries/list/useSearchPatientList";
import { useAppointmentsList } from "../../../../../queries/use-get-appointments";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  showToastOnApiCall,
  getFormattedId,
  getUnNecessaryTransformData,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
  transformMultiSelectBackEndVal,
} from "../../../../../utils/utils";
import {
  INSURANCE_PAYMENT_MODE_OPTIONS,
  PATIENT_PAYMENT_CATEGORY_OPTIONS,
  PATIENT_PAYMENT_INITIAL_VALUES,
  PATIENT_PAYMENT_MODE_OPTIONS,
} from "./patient-payments-constant";

import OutlineCard from "../../../../../components/OutlineCard";
import EnterSuperBills from "./EnterSuperBills";

import * as Yup from "yup";
import ProviderSelectScroll from "../../../../../components/ProviderSelectScroll";
import StyledFormModal from "../../../../../components/globalModal/StyledModal";
import { useSuperBillListV2 } from "../../../../../queries/billings/use-superBill-list-v2";
import EnterClaims from "./EnterClaims";
import { useGetClaimsV2 } from "../../../../../queries/billings/claims/use-get-claims-v2";
import useSelectedPatient from "../../../../../hooks/local-storage/use-selected-patient";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import NumberInput from "../../../../../components/NumberInput";

interface UpsertPatientPaymentsProps {
  disclosureHost: DisclosureHost;
  title?: string;
  prefillData?: any;
}

const UpsertPatientPayments = ({
  disclosureHost,
  title: _title,
  prefillData,
}: UpsertPatientPaymentsProps) => {
  const activePatientPayment = useQueryParams("activePatientPayment");
  const location = useLocation();

  // Check if current route contains "financial/payments"
  const isFinancialPaymentsRoute =
    location.pathname.includes("financial/payments");

  const { mutateAsync: addPayments } = useAddPayments();

  const [patient] = useSelectedPatient();
  const [me] = useCurrentUser();
  const [isSuperBillOpen, setIsSuperBillOpen] = useState(false);
  const [isClaimOpen, setIsClaimOpen] = useState(false);

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const validationSchema = Yup.object({
    patient: Yup.object().required().label("Patient Name"),
    amount: Yup.number()
      .min(1, "Amount must be at least 1")
      .required()
      .label("Amount"),
    paymentMode: Yup.string().required().label("Payment Mode"),
    collectedBy: Yup.object().required().label("Collected By"),
    note: Yup.string().label("Note"),
    paymentDate: Yup.string().required().label("Transaction Date"),
    insurancePayer: Yup.object().when([], {
      is: () => activePatientPayment === "insurance-payment",
      then: (schema) => schema.required().label("Insurance Payer"),
      otherwise: (schema) => schema.notRequired().label("Insurance Payer"),
    }),
  });

  const formik = useFormik({
    initialValues: prefillData ?? {
      ...PATIENT_PAYMENT_INITIAL_VALUES,
      patient: !patient
        ? undefined
        : {
            value: patient?.uuid,
            label: `${patient?.firstName} ${patient?.lastName}`,
          },
      // AC2 (ECRH-5090): "Collected By" auto-fills with the logged-in user,
      // but stays editable in case a different staff member collected it.
      collectedBy: {
        value: me?.uuid,
        label: `${me?.firstName} ${me?.lastName}`,
      },
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const getObj = (obj: { value: string; label: string }) =>
    obj ? { [obj.value]: obj.label } : undefined;

  async function handleSubmit(values?: any) {
    const selectedPatient = getObj(values.patient);
    const collectedBy = getObj(values.collectedBy);
    const mappedbillId = transformMultiSelectBackEndVal(values?.billId);
    const mappedVisitId = transformMultiSelectBackEndVal(values?.encounter);
    const mappedClaimId = transformMultiSelectBackEndVal(values?.claimId);
    // const mappedVisitId = getObj(values?.encounter);

    const typeOfPayment =
      activePatientPayment === "patient-payment"
        ? "PATIENT"
        : activePatientPayment === "insurance-payment"
          ? "INSURANCE"
          : isFinancialPaymentsRoute
            ? "PATIENT"
            : undefined;

    // EnterSuperBills is the only screen that splits a collected amount across
    // invoices, and it writes appliedAmount/unappliedAmount back into the form.
    // It is reachable from the billing Patient Payment tab only — the patient
    // chart's Collect Payment posted neither field, so the backend booked the
    // whole amount as applied. Nothing is allocated on that screen, so the full
    // amount is unapplied, matching the billing payload.
    const unappliedSplit =
      typeOfPayment === "PATIENT" && values?.unappliedAmount === null
        ? {
            appliedAmount: 0,
            unappliedAmount: Number(values?.amount) || 0,
          }
        : undefined;

    const payload = {
      patientPayment: getUnNecessaryTransformData(["referralDiagnosisCode"], {
        ...values,
        patient: selectedPatient,
        collectedBy: collectedBy,
        billIds: mappedbillId ?? undefined,
        visitIds: mappedVisitId ?? undefined,
        chequeId: values?.chequeId ?? undefined,
        transactionId: values?.transactionId ?? undefined,
        insurancePayer: values.insurancePayer?.insuranceId
          ? {
              payerId: values.insurancePayer?.insuranceId,
              payerName: values.insurancePayer?.label,
            }
          : undefined,
        typeOfPayment: typeOfPayment,
        ...unappliedSplit,
        // we made this fileds undefined because we are not sending billId and encounter in the payload
        billId: undefined,
        encounter: undefined,
        claimIds:
          activePatientPayment === "insurance-payment"
            ? (mappedClaimId ?? undefined)
            : undefined,
      }),
      xTENANTID: getXtenantId(),
    };
    // if (prefillData) showToastAndResetForm(updateReferral(payload));
    // else showToastAndResetForm(addPayments(payload));
    showToastAndResetForm(addPayments(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        // The payment listing is served by useGetPatientPaymentV2, whose key is
        // ["get-patient-payment-v2", ...]. The old ["get-patient-payment"] key
        // matched nothing, so the table only picked the new row up on a manual
        // page refresh.
        // queryClient.invalidateQueries({
        //   queryKey: ["get-patient-payment"],
        // });
        queryClient.invalidateQueries({
          queryKey: ["get-patient-payment-v2"],
        });
        // closeAndResetForm();
        // setIsSuperBillOpen(true);
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();
    // disclosureHost.close();
    disclosureHost.close();
  }

  const [patientSearch, setPatientSearch] = useDebounce<string>("");
  const [_, setSearchCodeKey] = useDebounce<string>("");
  const [superBillSearch, setSuperBillSearch] = useDebounce<string>("");

  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [visitPagination, setVisitPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [invoicePagination, setInvoicePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [claimPagination, setClaimPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const {
    data: patientList,
    isLoading: isLoadingPatients,
    isRefetching: isRefetchingPatients,
    isFetching: isFetchingPatients,
  } = useSearchPatientList({
    pagination: patientPagination,
    searchString: patientSearch,
    xTENANTID: getXtenantId(),
    disabled: !disclosureHost.opened,
  });

  const handlePatientSearch = (v: string) => {
    setPatientSearch(v);
    setPatientPagination((prev) => ({ ...prev, page: 0 }));
  };

  const {
    data: patientInsuraneData,
    isLoading: isLoadingInsurance,
    isFetching: isFetchingInsurance,
  }: any = useGetInsuranceByPatientId(getValueFromObj(formik?.values?.patient));

  const [isHovered, setIsHovered] = useState<string>();

  const {
    data: patientEncounterList,
    isLoading: isLoadingEncounterList,
    isFetching: isFetchingEncounterList,
  } = useAppointmentsList({
    pagination: visitPagination,
    providerGroupId: getXtenantId(),
    appointmentStatus: "COMPLETED",
    archieve: false,
    patientUuid: formik.values.patient?.value as any,
    searchKey: patientSearch,
    disabled: !disclosureHost.opened,
  });

  const { data: superBillList, isFetching: isFetchingSuperBillList } =
    useSuperBillListV2({
      pagination: invoicePagination,
      patientId: formik.values.patient?.value as any,
      searchKey: superBillSearch,
      enabled:
        disclosureHost.opened && activePatientPayment === "patient-payment",
      xTENANTID: getXtenantId(),
    });
  const { data: claimsList, isFetching: isFetchingClaimsList } = useGetClaimsV2(
    {
      pagination: claimPagination,
      patientUuids: [formik.values.patient?.value],
      searchKey: superBillSearch,
      enabled:
        disclosureHost.opened && activePatientPayment === "insurance-payment",
      gridSorting: [],
    }
  );

  const visitOptions = useMemo(() => {
    const fetched = formik.values.patient
      ? (patientEncounterList?.data?.content?.map((option) => ({
          value: option.uuid,
          label: ` ${
            option?.type ? replaceUnderScoreAddSpace(option?.type) : "-"
          }`,
          startTime: option?.encounterDate ?? "",
          chiefComplaint: option?.chiefComplaint ?? "",
          uuid: option.uuid,
        })) ?? [])
      : [];
    const selected = (formik.values.encounter ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [patientEncounterList, formik.values.patient, formik.values.encounter]);

  const invoiceOptions = useMemo(() => {
    const fetched = formik.values.patient
      ? (superBillList?.data?.content?.map((it: any) => ({
          value: it?.uuid,
          label: `${getFormattedId(it?.uuid)} - ${formatDateTime(
            it?.billDate
          )}`,
        })) ?? [])
      : [];
    const selected = (formik.values.billId ?? []) as { [k: string]: string }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [superBillList, formik.values.patient, formik.values.billId]);

  const claimOptions = useMemo(() => {
    const fetched = formik.values.patient
      ? (claimsList?.data?.content?.map((it: any) => ({
          value: it?.uuid,
          label: `${getFormattedId(it?.uuid)} - ${formatDateTime(
            it?.dateOfService
          )}`,
        })) ?? [])
      : [];
    const selected = (formik.values.claimId ?? []) as { [k: string]: string }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [claimsList, formik.values.patient, formik.values.claimId]);

  useEffect(() => {}, [formik.values, formik.errors]);

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndResetForm}
      dialogTitle="Collect Patient Payment"
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "70vh",
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} pt={2}>
          {/* Patient — AC1 (ECRH-5090): hidden when the patient is already in
              context (chart / financials); still shown for the standalone
              billings & insurance-payment screens where none is preselected. */}
          {(!patient?.uuid ||
            (!!activePatientPayment &&
              activePatientPayment === "insurance-payment")) && (
            <Grid item xs={4}>
              <AsyncSelectScroll
                required
                loading={
                  isRefetchingPatients ||
                  isLoadingPatients ||
                  isFetchingPatients
                }
                label="Patient"
                name="patient"
                handleBlur={formik.handleBlur}
                placeholder="Search Patient"
                enableFilterOption
                renderCustomLabel={(option) => (
                  <Flex flexDirection={"column"}>
                    <Text>{option.label}</Text>
                  </Flex>
                )}
                options={patientList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.firstName + " " + it?.lastName,
                }))}
                value={formik.values?.patient}
                onSearch={handlePatientSearch}
                onChange={(val) => {
                  formik.setFieldValue("patient", val);
                  formik.setFieldValue("encounter", []);
                  formik.setFieldValue("billId", []);
                  formik.setFieldValue("insurancePayer", undefined);
                }}
                error={formik.touched.patient && (formik.errors.patient as any)}
                pagination={patientPagination}
                setPagination={setPatientPagination}
                totalElements={patientList?.data?.page?.totalElements}
              />
            </Grid>
          )}
          {/* Collected By — AC2 (ECRH-5090): replaces the Provider field;
              auto-filled with the logged-in user, editable. */}
          <Grid item xs={4}>
            <ProviderSelectScroll
              required
              label="Collected By"
              value={formik.values.collectedBy}
              onChange={(val: any) => formik.setFieldValue("collectedBy", val)}
              error={
                formik.touched.collectedBy && (formik.errors.collectedBy as any)
              }
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              label="Amount"
              required
              textFieldProps={{
                placeholder: "Enter Amount",
                name: "amount",
                value: formik.values.amount,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                // Currency symbol so the amount input matches how amounts are
                // rendered everywhere else in billing.
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
              error={formik.touched.amount && (formik.errors.amount as any)}
            />
          </Grid>
          {/* Transaction Date — AC5 (ECRH-5090): renamed from "Payment Date". */}
          <Grid item xs={4}>
            <CustomDatePicker
              name="paymentDate"
              handleBlur={formik.handleBlur}
              label="Transaction Date"
              maxDate={dayjs(new Date())}
              value={formik.values.paymentDate}
              onChange={(val) => formik.setFieldValue("paymentDate", val)}
              placeholder="Choose Date"
              required
              error={
                formik.touched.paymentDate && (formik.errors.paymentDate as any)
              }
            />
          </Grid>
          {/* payer name */}
          {activePatientPayment === "insurance-payment" && (
            <Grid item xs={4}>
              <AsyncSelect
                required
                loading={isLoadingInsurance || isFetchingInsurance}
                label="Payer Name"
                options={
                  formik.values.patient
                    ? patientInsuraneData?.content?.map((it: any) => ({
                        value: it?.uuid,
                        label: `${it?.insurancePayer?.payerName}`,
                        insuranceId: it?.insurancePayer?.payerId,
                      }))
                    : []
                }
                enableFilterOption
                renderCustomLabel={(option) => (
                  <Flex flexDirection={"column"}>
                    <Text>{option.label}</Text>
                  </Flex>
                )}
                onSearch={setSearchCodeKey}
                placeholder="Search & Select Payer Name"
                value={formik.values.insurancePayer}
                onChange={(val) => formik.setFieldValue("insurancePayer", val)}
                error={
                  formik.touched.insurancePayer &&
                  (formik.errors.insurancePayer as any)
                }
              />
            </Grid>
          )}
          {/* Payment Mode — AC3/AC4 (ECRH-5090): Payment Method dropdown removed;
              Cheque now lives here as one of the mode values. */}
          <Grid item xs={4}>
            <CustomSelect
              required
              name="paymentMode"
              handleBlur={formik.handleBlur}
              label="Payment Mode"
              placeholder="Payment Mode"
              options={
                activePatientPayment === "insurance-payment"
                  ? INSURANCE_PAYMENT_MODE_OPTIONS
                  : PATIENT_PAYMENT_MODE_OPTIONS
              }
              value={formik.values?.paymentMode}
              onChange={(v) => formik.setFieldValue("paymentMode", v)}
              error={
                formik.touched.paymentMode && (formik.errors.paymentMode as any)
              }
            />
          </Grid>
          {/* Category — AC5 (ECRH-5090): optional, aligns with the Category column. */}
          <Grid item xs={4}>
            <CustomSelect
              label="Category"
              placeholder="Select Category"
              options={PATIENT_PAYMENT_CATEGORY_OPTIONS}
              value={formik.values?.paymentCategory}
              onChange={(v) => formik.setFieldValue("paymentCategory", v)}
              error={
                formik.touched.paymentCategory &&
                (formik.errors.paymentCategory as any)
              }
            />
          </Grid>
          {/* Row 4: Note */}
          <Grid item xs={12}>
            <CustomTextArea
              label="Note"
              placeholder="Note"
              name="note"
              value={formik.values.note}
              onChange={formik.handleChange}
              minRows={2}
            />
          </Grid>
          {/* Row 5: viste ID ( encounter ID ) */}
          <Grid item xs={4}>
            <MaskMultiSelectScroll
              label="Visit"
              loading={isLoadingEncounterList || isFetchingEncounterList}
              options={visitOptions}
              pagination={visitPagination}
              setPagination={setVisitPagination}
              totalElements={patientEncounterList?.data?.page?.totalElements}
              renderCustomLabel={(option) => {
                const time = option?.startTime
                  ? formatDateTime(option?.startTime)
                  : "-";
                const HoveredBox = (option: any) =>
                  `Appointment Type: ${replaceUnderScoreAddSpace(
                    option.label
                  )}, Date & Time:${time}, Reason for Visit: ${
                    option.chiefComplaint || "-"
                  }`;

                return (
                  <Box
                    onMouseEnter={() => setIsHovered(option.uuid)}
                    onMouseLeave={() => setIsHovered(undefined)}
                  >
                    <Text>{replaceUnderScoreAddSpace(option.label)}</Text>
                    <Text>{time}</Text>
                    {isHovered === option.uuid && (
                      <OutlineCard>{HoveredBox(option)}</OutlineCard>
                    )}
                  </Box>
                );
              }}
              onSearch={setPatientSearch}
              placeholder="Search & Select Visit"
              value={formik.values.encounter}
              onChange={(val) => formik.setFieldValue("encounter", val)}
              error={
                formik.touched.encounter && (formik.errors.encounter as any)
              }
            />
          </Grid>
          {/* Row 5:  Invoice ID */}
          {activePatientPayment === "patient-payment" && (
            <Grid item xs={4}>
              <MaskMultiSelectScroll
                label="Invoice ID"
                loading={isFetchingSuperBillList}
                options={invoiceOptions}
                pagination={invoicePagination}
                setPagination={setInvoicePagination}
                totalElements={superBillList?.data?.page?.totalElements}
                onSearch={setSuperBillSearch}
                placeholder="Search & Select Invoice ID"
                value={formik.values.billId}
                onChange={(val) => formik.setFieldValue("billId", val)}
                error={formik.touched.billId && (formik.errors.billId as any)}
              />
            </Grid>
          )}
          {/* Row 5:  Claim ID */}
          {activePatientPayment === "insurance-payment" && (
            <Grid item xs={4}>
              <MaskMultiSelectScroll
                label="Claim ID"
                loading={isFetchingClaimsList}
                options={claimOptions}
                pagination={claimPagination}
                setPagination={setClaimPagination}
                totalElements={claimsList?.data?.page?.totalElements}
                onSearch={setSuperBillSearch}
                placeholder="Search & Select Claim ID"
                value={formik.values.claimId}
                onChange={(val) => formik.setFieldValue("claimId", val)}
                error={formik.touched.claimId && (formik.errors.claimId as any)}
              />
            </Grid>
          )}
          <Grid item xs={1}>
            <Button
              sx={{ width: "100%" }}
              variant="contained"
              onClick={() => {
                if (activePatientPayment === "patient-payment") {
                  setIsSuperBillOpen(true);
                } else {
                  setIsClaimOpen(true);
                }
              }}
            >
              Add
            </Button>
          </Grid>
          {isSuperBillOpen && (
            <Grid item xs={12}>
              <EnterSuperBills
                formik={formik}
                closeAndResetForm={closeAndResetForm}
                onClose={() => {
                  setIsSuperBillOpen(false);
                  closeAndResetForm();
                }}
              />
            </Grid>
          )}
          {isClaimOpen && (
            <Grid item xs={12}>
              <EnterClaims
                closeAndResetForm={closeAndResetForm}
                onClose={() => {
                  setIsClaimOpen(false);
                  closeAndResetForm();
                }}
                formik={formik}
              />
            </Grid>
          )}
        </Grid>
      </form>
    </StyledFormModal>
  );
};

export default UpsertPatientPayments;
